import { IconChevronDown16 } from '@warp-ds/icons/react';
import cn from 'classnames';
import type { ReactNode } from 'react';
import { forwardRef } from 'react';

import styles from './TriggerButton.module.css';

export interface TriggerProps {
    triggerLabel?: string;
    text: string;
    prefix?: ReactNode;
    onTrigger: () => void;
    open?: boolean;
    slim?: boolean;
    aria?: {
        label?: string;
        hasPopup: 'listbox' | 'dialog';
        controls?: string;
    };
    hideChevron?: boolean;
    error?: string | null;
    hideLabelOnSmallScreen?: boolean;
}

export const TriggerButton = forwardRef<HTMLButtonElement, TriggerProps>(function TriggerButton(
    { triggerLabel, text, aria, prefix, onTrigger, slim = false, open, hideChevron = false, error, hideLabelOnSmallScreen = false },
    ref,
) {
    return (
        <div
            className={cn(styles.wrapper, {
                [styles['wrapper--slim']]: slim,
            })}>
            {triggerLabel && (
                <strong
                    className={cn(styles['trigger-label'], {
                        [styles['trigger-label--hide-on-small']]: hideLabelOnSmallScreen,
                    })}>
                    {triggerLabel}
                </strong>
            )}
            <button
                ref={ref}
                type="button"
                onClick={onTrigger}
                className={cn(styles.trigger, {
                    [styles['trigger--slim']]: slim,
                    [styles['trigger--dialog']]: aria?.hasPopup === 'dialog',
                    [styles['trigger--error']]: error,
                })}
                {...(aria?.hasPopup ? { 'aria-haspopup': aria.hasPopup } : {})}
                {...(typeof open === 'boolean' ? { 'aria-expanded': open } : {})}
                {...(aria?.controls && open ? { 'aria-controls': aria.controls } : {})}
                {...(aria?.label ? { 'aria-label': aria.label } : {})}>
                <span className={styles.trigger__text}>
                    {prefix && <span className={styles.prefix}>{prefix}</span>}
                    {text && <span className={styles.text}>{text}</span>}
                </span>
                <IconChevronDown16
                    aria-hidden
                    className={
                        hideChevron ? 'hidden' : aria?.hasPopup === 'listbox' && open ? styles['chevron--open'] : styles['chevron--closed']
                    }
                />
            </button>
            {error && <span className={styles.error}>{error}</span>}
        </div>
    );
});
